import { AutoMode } from '@mui/icons-material';
import { Box, styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomePageStats } from 'Types/stats';

import ChatStatsIcon from '../../assets/chat-stats-icon.svg';
import DefaultUserStatsIcon from '../../assets/default-user-stats-icon.svg';
import MessagesStatsIcon from '../../assets/messages-stats-icon.svg';
import { getHomePageStats } from '../../services/stats';
import { Conversation } from '../../Types/conversation';
import { AppEnum } from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import { useAppProvider } from '../Providers/AppProvider';

interface TitleBoxProps {
  useFunctions: boolean;
}

const TitleBox = styled('div')<TitleBoxProps>(({ useFunctions }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'start',
  marginBottom: '30px',
  marginTop: !useFunctions ? '30px' : '0px',
  width: '70%',
}));

const InfoCard = styled(Box)({
  width: '280px',
  minHeight: '305px',
  textAlign: 'start',
  display: 'flex',
  flexDirection: 'column',

  h4: {
    marginLeft: '20px',
    marginTop: '30px',
    marginBottom: '20px',
  },
});

const Link = styled('span')({
  textDecoration: 'underline',
  ':hover': {
    cursor: 'pointer',
  },
});

const StatsContainer = styled('div')({
  width: '100%',
  justifyContent: 'flex-start',
  display: 'flex',
});

const StyledStatsIcon = styled('img')({
  width: '60px',
  marginRight: '20px',
});

export type ChatInfoProps = {
  currentConversation?: Conversation;
  currentPrompt?: Prompt;
  shouldShowInfo: boolean;
  useFunctions: boolean;
};

const ChatInfo = ({
  currentConversation,
  currentPrompt,
  shouldShowInfo,
  useFunctions,
}: ChatInfoProps) => {
  const { appState } = useAppProvider();

  const [stats, setStats] = useState<HomePageStats>({
    numUsers: 0,
    numActiveConversations: 0,
    numMessages: 0,
  });
  const [appName, setAppName] = useState(
    appState.selectedApp === AppEnum.IMAGPT ? 'IMA GPT' : 'Producer Playbook'
  );
  const [shouldFetchStats, setShouldFetchStats] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    switch (appState.selectedApp) {
      case AppEnum.IMAGPT:
        setAppName('IMA GPT');
        break;
      case AppEnum.PRODUCER_PLAYBOOK:
        setAppName('Producer Playbook');
        break;
    }
  }, [appState.selectedApp]);

  useEffect(() => {
    const fetchStats = async () => {
      const newStats = await getHomePageStats();
      setStats(newStats);
      setShouldFetchStats(false);
    };
    if (shouldFetchStats) {
      fetchStats();
    }
  }, [shouldFetchStats]);

  return (
    <div>
      {currentPrompt?.userEditRequired === false ||
      (currentConversation && currentConversation.prompt?.userEditRequired === false) ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div style={{ width: '70% ' }}>
            <AutoMode sx={{ width: '36px', height: '36px', marginY: '30px' }} />
            <Typography sx={{ fontWeight: '700', fontSize: '36px', marginBottom: '30px' }}>
              {currentPrompt
                ? currentPrompt.title
                : currentConversation && currentConversation.prompt?.title}
            </Typography>
            <Typography sx={{ fontSize: '20px', marginBottom: '30px' }}>
              {currentPrompt
                ? currentPrompt.description
                : currentConversation && currentConversation.prompt?.description}
            </Typography>
          </div>
        </div>
      ) : shouldShowInfo ? (
        <div>
          <TitleBox useFunctions={useFunctions}>
            <Typography
              variant="h4"
              sx={{ marginBottom: '10px', fontWeight: '700', fontSize: '36px' }}
            >
              {`Welcome to the ${appName} chat`}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontSize: '16px' }}>
              {appName === 'IMA GPT'
                ? `${appName} is a conversational AI chatbot you can use to generate ideas, create draft presentations, or just brainstorm. Happy chatting!`
                : `This chat is for you to ask any questions you have about the Producer Playbook. Happy Chatting!`}
            </Typography>
          </TitleBox>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: '10px',
              marginBottom: '20px',
            }}
          >
            <InfoCard sx={{ bgcolor: '#003665', '&:hover': { cursor: 'default' } }}>
              <Typography
                variant="h4"
                sx={{ color: '#b78a1c', fontWeight: '700', fontSize: '24px' }}
              >
                IMA All User Stats
              </Typography>
              <div
                style={{
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  margin: '0 20px 20px 20px',
                  gap: '30px',
                  display: 'flex',
                }}
              >
                <StatsContainer>
                  <StyledStatsIcon src={DefaultUserStatsIcon} />
                  <Typography style={{ color: 'white' }}>
                    <span style={{ fontWeight: 'bold' }}>{stats.numUsers}</span> Users Total
                  </Typography>
                </StatsContainer>
                <StatsContainer>
                  <StyledStatsIcon src={ChatStatsIcon} />
                  <Typography style={{ color: 'white' }}>
                    <span style={{ fontWeight: 'bold' }}>{stats.numActiveConversations}</span> Chats
                    Started Today
                  </Typography>
                </StatsContainer>
                <StatsContainer>
                  <StyledStatsIcon src={MessagesStatsIcon} style={{ filter: 'invert(1)' }} />
                  <Typography style={{ color: 'white' }}>
                    <span style={{ fontWeight: 'bold' }}>{stats.numMessages}</span> Total Messages
                    Sent Since Launch
                  </Typography>
                </StatsContainer>
              </div>
            </InfoCard>
            <InfoCard sx={{ bgcolor: '#003665' }}>
              <Typography
                variant="h4"
                sx={{ color: '#b78a1c', fontWeight: '700', fontSize: '24px' }}
              >
                My Prompts
              </Typography>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  margin: '0 20px 20px 20px',
                  flexGrow: 1,
                }}
              >
                <Typography style={{ color: 'white' }}>
                  Be sure to check out the personal prompts page, a.k.a.{' '}
                  <Link onClick={() => navigate('/my-prompts')}>My Prompts</Link>
                  . <br />
                  <br />
                  Think of them as the place to store your commonly asked questions or tasks.
                </Typography>
              </div>
            </InfoCard>
            <InfoCard sx={{ bgcolor: '#003665' }}>
              <Typography
                variant="h4"
                sx={{ color: '#b78a1c', fontWeight: '700', fontSize: '24px' }}
              >
                FAQ
              </Typography>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  margin: '0 20px 20px 20px',
                  flexGrow: 1,
                }}
              >
                <Typography style={{ color: 'white' }}>
                  Not sure where to start? Check out our index of{' '}
                  <Link onClick={() => navigate('/faqs')}>common questions</Link> — with super-smart
                  answers!
                </Typography>
              </div>
            </InfoCard>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ChatInfo;
